import { useEffect, useState } from "react";

export const ONE_TRUST_SCRIPT_STATUS_EVENT = "OneTrustScriptStatusChanged";

export type OneTrustScriptStatus =
  | "disabled"
  | "loading"
  | "ready"
  | "failed";

type OneTrustStatusWindow = Window & {
  __zipOneTrustScriptStatus?: OneTrustScriptStatus;
  OneTrust?: unknown;
  Optanon?: unknown;
  OnetrustActiveGroups?: string;
};

const getBrowserWindow = (): OneTrustStatusWindow | undefined =>
  globalThis.window ?? undefined;

export function hasOneTrustSdkGlobal(): boolean {
  const oneTrustWindow = getBrowserWindow();
  if (!oneTrustWindow) return false;

  return Boolean(
    oneTrustWindow.OneTrust ||
      oneTrustWindow.Optanon ||
      oneTrustWindow.OnetrustActiveGroups,
  );
}

export function setOneTrustScriptStatus(status: OneTrustScriptStatus): void {
  const oneTrustWindow = getBrowserWindow();
  if (!oneTrustWindow) return;

  oneTrustWindow.__zipOneTrustScriptStatus = status;
  oneTrustWindow.dispatchEvent(
    new CustomEvent<OneTrustScriptStatus>(ONE_TRUST_SCRIPT_STATUS_EVENT, {
      detail: status,
    }),
  );
}

export function getOneTrustScriptStatus(
  domainScript: string | undefined,
): OneTrustScriptStatus {
  if (!domainScript) return "disabled";
  const oneTrustWindow = getBrowserWindow();
  if (!oneTrustWindow) return "loading";

  const existingStatus = oneTrustWindow.__zipOneTrustScriptStatus;
  if (existingStatus) {
    if (existingStatus !== "ready" && hasOneTrustSdkGlobal()) {
      return "ready";
    }

    if (existingStatus === "disabled") {
      return "loading";
    }

    return existingStatus;
  }

  return hasOneTrustSdkGlobal() ? "ready" : "loading";
}

export function useOneTrustScriptStatus(
  domainScript: string | undefined,
): OneTrustScriptStatus {
  const [status, setStatus] = useState<OneTrustScriptStatus>(() =>
    getOneTrustScriptStatus(domainScript),
  );

  useEffect(() => {
    setStatus(getOneTrustScriptStatus(domainScript));
    const oneTrustWindow = getBrowserWindow();
    if (!domainScript || !oneTrustWindow) return;

    const handler = (event: Event) => {
      setStatus(getOneTrustScriptStatus(domainScript));
    };

    oneTrustWindow.addEventListener(ONE_TRUST_SCRIPT_STATUS_EVENT, handler);
    return () => {
      oneTrustWindow.removeEventListener(ONE_TRUST_SCRIPT_STATUS_EVENT, handler);
    };
  }, [domainScript]);

  return status;
}
